import React, { useCallback } from 'react'

import type { AdapterKitControlProps } from 'components/formKit/types'
import Divider from 'components/uiKit/Divider'
import { isMixed } from 'components/uiKit/KitUtils'
import type { ITimePickerProps } from 'components/uiKit/TimePicker/TimePicker'
import TimePicker from 'components/uiKit/TimePicker/TimePicker'
import { determineTimeFormat } from 'utils/editorUtils'

import * as s from './FieldTimeRange.module.scss'

const FieldTimeRange = ({
  onChange,
  name,
  defaultValue,
  value = defaultValue,
  maxTime,
  ...rest
}: AdapterKitControlProps<ITimePickerProps, [number, number] | null>) => {
  const safeValue = isMixed(value) ? null : value
  const [start = 0, finish = maxTime] = safeValue || []
  const format = determineTimeFormat(finish || 0)

  const handleChange = useCallback(
    (value: [number, number]) => onChange({ value, name }),
    [name, onChange],
  )

  const handleTimeStart = useCallback(
    (value: number | null) => handleChange([Math.min(value || 0, finish || Infinity), finish || 0]),
    [handleChange, finish],
  )

  const handleTimeFinish = useCallback(
    (value: number | null) => handleChange([start || 0, Math.max(value || maxTime || 0, start)]),
    [handleChange, start, maxTime],
  )

  const handleResetStart = () => handleChange([0, finish || maxTime || 0])

  const handleResetFinish = () => handleChange([start || 0, Math.trunc(maxTime || Infinity)])

  if (isMixed(value)) {
    return null
  }

  return (
    <div className={s.root}>
      <TimePicker
        {...rest}
        format={format}
        maxTime={Math.min(finish || Infinity, maxTime || Infinity)}
        name='timeStart'
        onChange={handleTimeStart}
        onReset={handleResetStart}
        value={start}
        hideIcon
      />
      <Divider styleType='dash' />
      <TimePicker
        {...rest}
        format={format}
        maxTime={maxTime}
        minTime={start}
        name='timeEnd'
        onChange={handleTimeFinish}
        onReset={handleResetFinish}
        value={finish || 0}
        hideIcon
      />
    </div>
  )
}

export default React.memo(FieldTimeRange) as typeof FieldTimeRange
